import { useWorkoutDurations } from '../../hook/use-workouts'
import { WorkoutFilter } from '../../service/core-service'
import { useMemo } from 'react'

export type PredefinedSectionSlug = 'dumbbell' | 'bodyweight' | 'barbell' | 'express'

export interface PredefinedSection {
  slug: PredefinedSectionSlug
  label: string
  /** Number of preview cards on the sections home (GH-754: Dumbbell/Bodyweight 5, Barbell/Express 3). */
  previewCount: number
  /** Mosaic side for the featured (large) preview card; unset renders the standard 3-up row. */
  featured?: 'left' | 'right'
}

/** The four predefined sections in fixed display order (GH-754). */
export const PREDEFINED_SECTIONS: PredefinedSection[] = [
  { slug: 'dumbbell', label: 'Dumbbell', previewCount: 5, featured: 'left' },
  { slug: 'bodyweight', label: 'Bodyweight', previewCount: 5, featured: 'right' },
  { slug: 'barbell', label: 'Barbell', previewCount: 3 },
  { slug: 'express', label: 'Express Workouts', previewCount: 3 },
]

// URL slug → equipment group `name`. Capitalized to match the catalog values the filter panel
// sends (verified against the GH-774 seed: Dumbbell / Bodyweight / Barbell).
const EQUIPMENT_NAME_BY_SLUG: Record<Exclude<PredefinedSectionSlug, 'express'>, string> = {
  dumbbell: 'Dumbbell',
  bodyweight: 'Bodyweight',
  barbell: 'Barbell',
}

export const isPredefinedSectionSlug = (slug: string | undefined): slug is PredefinedSectionSlug =>
  PREDEFINED_SECTIONS.some((section) => section.slug === slug)

export interface PredefinedSectionFilter {
  /** The resolved filter, or undefined once settled with no resolvable filter (e.g. no express durations). */
  filter: WorkoutFilter | undefined
  /** True while the inputs needed to resolve the filter are still loading (Express → durations catalog). */
  isLoading: boolean
}

/**
 * Resolves a predefined section slug to its `WorkoutFilter` equivalence (GH-754):
 * Dumbbell/Bodyweight/Barbell → `equipmentContains=[name]`; Express → `durations=[express duration ids]`.
 *
 * Browse sections use `equipmentContains` (a "contains" match) rather than `equipment` (the panel's
 * subset/"equipment I have" match): a section like "Barbell" should surface every workout that uses a
 * barbell, including mixed-equipment ones (e.g. barbell + dumbbell) — the subset match excludes those
 * and would leave the section empty.
 *
 * Express depends on the durations catalog, so `isLoading` reflects that fetch — letting callers
 * tell "still loading" apart from "settled, no filter" (durations failed / none flagged express)
 * and avoid hanging on skeletons.
 */
export const usePredefinedSectionFilter = (slug: PredefinedSectionSlug): PredefinedSectionFilter => {
  const { data: durations = [], isLoading: areDurationsLoading } = useWorkoutDurations()
  return useMemo<PredefinedSectionFilter>(() => {
    if (slug !== 'express') {
      return { filter: { equipmentContains: [EQUIPMENT_NAME_BY_SLUG[slug]] }, isLoading: false }
    }
    const expressDurationIds = durations.filter((duration) => duration.isExpressDuration).map((duration) => duration.id)
    return {
      filter: expressDurationIds.length ? { durations: expressDurationIds } : undefined,
      isLoading: areDurationsLoading,
    }
  }, [slug, durations, areDurationsLoading])
}
